import { SHARED_UI_LABELS } from '@/config/task-display-config';

/**
 * The CLOSED vocabulary of outcomes `POST /api/work/inbox/{item_id}/accept` can report, and
 * what each one means for the screen.
 *
 * WI-IDEA-LANE-EXECUTE-CEO-RULINGS-001, item A. CEO ruling 2026-07-30.
 *
 * WHY THIS EXISTS AS A TABLE RATHER THAN AN if/else CHAIN.
 * `onAccept` used to branch inline: `status === 'approved'` -> success, `status === 'ignored'`
 * -> "already processed", **else -> error**. That shape is an ALLOW-LIST wearing a switch's
 * clothes: every status literal the route grows silently joins the error branch. It did.
 * The failure-receipt acknowledge action returns `"acknowledged"` after a write that LANDS,
 * so the CEO was shown "Something went wrong, please try again" over a successful write, and
 * `refresh()` never ran because it lived only in the 'approved' branch. He reported his
 * buttons as dead. They were not — one word was missing from a list.
 *
 * A table makes the omission testable. `acceptStatusVocabulary.test.js` enumerates every
 * literal the route can return and fails if any lacks a decided branch, so the NEXT literal
 * cannot repeat this quietly. The class, not the instance.
 *
 * `refresh` is declared per outcome rather than inferred from `success`, because they are
 * genuinely different questions: "did it work" and "must the list be re-read". They happen to
 * agree today; coupling them would hide the day they do not.
 */
export const ACCEPT_STATUS_OUTCOMES = {
    approved: {
        success: true,
        refresh: true,
        // The specific sentence is chosen by the caller from the work type + action; this is
        // the fallback when none applies.
        message: SHARED_UI_LABELS.acceptOutcomeApproved,
    },
    acknowledged: {
        success: true,
        refresh: true,
        message: SHARED_UI_LABELS.acceptOutcomeAcknowledged,
    },
    ignored: {
        success: false,
        refresh: false,
        message: SHARED_UI_LABELS.acceptOutcomeIgnored,
    },
    blocked: {
        success: false,
        refresh: false,
        // Deliberately does NOT surface the backend's `reason` token
        // (`send_blocked_unauthenticated`). The CEO reads this surface by text-to-speech and a
        // snake_case token is not a sentence.
        message: SHARED_UI_LABELS.acceptOutcomeBlocked,
        suppressBackendReason: true,
    },
    failed: {
        success: false,
        refresh: false,
        message: SHARED_UI_LABELS.acceptOutcomeFailed,
    },
};

/**
 * Resolve a response into what the screen should do. An UNDECLARED status is never treated as
 * success — it fails closed, exactly as the old `else` branch did, but it is now the
 * documented default rather than an accident of where the chain ended.
 */
export function resolveAcceptOutcome(data) {
    const status = data?.status;
    const declared = ACCEPT_STATUS_OUTCOMES[status];

    if (!declared) {
        return {
            success: false,
            refresh: false,
            message: data?.reason || SHARED_UI_LABELS.acceptOutcomeUndeclared,
        };
    }

    // A backend-supplied reason is a real sentence written for this case and beats the generic
    // one — except where the outcome declares its token unfit to show.
    const message =
        !declared.suppressBackendReason && data?.reason ? data.reason : declared.message;

    return { success: declared.success, refresh: declared.refresh, message };
}
